import React, { useEffect, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {Box, Paper, Typography, Button, Chip, Divider} from "@mui/material";
import {
    HourglassEmpty as PendingIcon,
    CheckCircleOutlined as ApprovedIcon,
    HighlightOff as RejectedIcon,
    CalendarMonth as DaysIcon,
    GroupOutlined as EmployeesIcon,
    PersonOff as OnLeaveIcon,
    Layers as TypesIcon,
    Celebration as HolidayIcon,
    Refresh as RefreshIcon,
    AssignmentLate as QueueIcon,
} from "@mui/icons-material";
import {BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell} from "recharts";
import HeaderBannerPage from "../../components/HeaderBannerPage";
import { fetchLeaveRequests } from "../../actions/Leave/LeaveRequestAction";
import { fetchLeaveTypes } from "../../actions/Leave/LeaveTypeAction";
import { fetchHolidays } from "../../actions/Leave/HolidayAction";

//Helpers

const NAVY = "#142c54";
const STATUS_COLORS = { Pending: "#f59e0b", Approved: "#059669", Rejected: "#dc2626" };
const PAID_COLORS = { Paid: "#059669", "No Pay": "#d97706" };

const toDate = (s) => new Date(`${s}T00:00:00`);
const fmtDate = (s) =>
    toDate(s).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
const fmtDays = (n) => `${n} ${n === 1 ? "day" : "days"}`;
const sum = (list) => list.reduce((a, b) => a + b, 0);

const tooltipStyle = { borderRadius: 8, fontSize: 12, border: "1px solid #e5e7eb" };
const cardSx = {
    p: 2.5,
    borderRadius: "12px",
    border: "1px solid #eef0f4",
    boxShadow: "0px 2px 8px rgba(20, 44, 84, 0.06)",
    backgroundColor: "#fff",
};

//small compoenents

const StatCard = ({ label, value, sub, icon: Icon, color }) => (
    <Paper elevation={0} sx={{ ...cardSx, p: 2 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#64748b", letterSpacing: 0.3 }}>
            {label}
        </Typography>
        <Box
            sx={{
            width: 30, height: 30, borderRadius: "8px", display: "flex",
            alignItems: "center", justifyContent: "center",
            backgroundColor: `${color}1A`, color,
            }}
        >
            <Icon sx={{ fontSize: 18 }} />
        </Box>
        </Box>
        <Typography sx={{ fontSize: 28, fontWeight: 700, color: NAVY, mt: 1, lineHeight: 1.1 }}>
        {value}
        </Typography>
        <Typography sx={{ fontSize: 11.5, color: "#94a3b8", mt: 0.5 }} noWrap title={sub}>
        {sub}
        </Typography>
    </Paper>
);

const ChartCard = ({ title, subtitle, empty, footer, children }) => (
    <Paper
        elevation={0}
        sx={{
        ...cardSx,
        height: "100%",
        minWidth: 0,
        overflow: "hidden",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        }}
    >
        <Typography sx={{ fontSize: 15, fontWeight: 700, color: NAVY }}>{title}</Typography>
        {subtitle && (
        <Typography sx={{ fontSize: 12, color: "#94a3b8", mb: 1.5 }}>{subtitle}</Typography>
        )}
        {empty ? (
        <Box sx={{ height: 180, display: "flex", alignItems: "center", justifyContent: "center", color: "#94a3b8", fontSize: 13 }}>
            No data
        </Box>
        ) : (
        children
        )}
        {footer && <Box sx={{ mt: "auto", pt: 1 }}>{footer}</Box>}
    </Paper>
);

const Legend = ({ items }) => (
    <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: 1.5, fontSize: 12, color: "#475569" }}>
        {items.map((d) => (
        <Box key={d.name} sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: d.color }} />
            {d.name} <b style={{ color: "#1e293b" }}>{d.value}</b>
        </Box>
        ))}
    </Box>
);

const DonutChart = ({ data }) => (
    <ResponsiveContainer width="100%" height={190}>
        <PieChart>
        <Pie data={data} dataKey="value" nameKey="name" innerRadius={48} outerRadius={74} paddingAngle={2}>
            {data.map((d, i) => <Cell key={i} fill={d.color} />)}
        </Pie>
        <Tooltip contentStyle={tooltipStyle} />
        </PieChart>
    </ResponsiveContainer>
);

const ListRow = ({ title, subtitle, right, rightSx }) => (
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, py: 1.25 }}>
        <Box sx={{ minWidth: 0 }}>
        <Typography noWrap sx={{ fontSize: 13.5, fontWeight: 600, color: "#1e293b" }}>{title}</Typography>
        <Typography noWrap sx={{ fontSize: 11.5, color: "#94a3b8" }}>{subtitle}</Typography>
        </Box>
        <Box sx={{ flexShrink: 0, fontSize: 12, ...rightSx }}>{right}</Box>
    </Box>
);

const LeaveDashboard = () => {
    const dispatch = useDispatch();
    const { data: requests = [], loading: requestsLoading } = useSelector((s) => s.leaveRequests) || {};
    const { data: rawTypes = [], loading: typesLoading } = useSelector((s) => s.leaveTypes) || {};
    const { data: rawHolidays = [], loading: holidaysLoading } = useSelector((s) => s.leaveHolidays) || {};

    const loadAll = useCallback(() => {
        dispatch(fetchLeaveRequests());
        dispatch(fetchLeaveTypes());
        dispatch(fetchHolidays());
    }, [dispatch]);

    useEffect(() => { loadAll(); }, [loadAll]);

    const refreshing = requestsLoading || typesLoading || holidaysLoading;
    const handleRefresh = loadAll;

    // leave types with plain true/false flags, and only the active holidays
    const types = useMemo(() => rawTypes.map((t) => ({ ...t, paid: t.isPaid, active: t.isActive })), [rawTypes]);
    const holidays = useMemo(() => rawHolidays.filter((h) => h.active), [rawHolidays]);
    const typeMap = useMemo(() => Object.fromEntries(types.map((t) => [t.id, t])), [types]);

    const analytics = useMemo(() => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const r2 = (n) => Math.round(n * 100) / 100;
        const by = (s) => requests.filter((x) => x.status === s);
        const pending = by("Pending");
        const approved = by("Approved");
        const rejected = by("Rejected");
        const decided = approved.length + rejected.length;

        // leave that is happening or still waiting for a decision (cancelled and rejected leave is left out)
        const live = [...approved, ...pending];

        const onLeaveToday = new Set(
            approved
                .filter((x) => x.from && toDate(x.from) <= today && toDate(x.to || x.from) >= today)
                .map((x) => x.serviceNo)
        ).size;

        const upcoming = holidays
            .filter((h) => h.date && toDate(h.date) >= today)
            .sort((a, b) => a.date.localeCompare(b.date));

        const kpis = {
            pending: pending.length,
            pendingDays: r2(sum(pending.map((x) => x.days))),
            approved: approved.length,
            approvedDays: r2(sum(approved.map((x) => x.days))),
            rejected: rejected.length,
            approvalRate: decided ? (approved.length / decided) * 100 : 0,
            totalDays: r2(sum(requests.map((x) => x.days))),
            employees: new Set(requests.map((x) => x.serviceNo).filter(Boolean)).size,
            onLeaveToday,
            activeTypes: types.filter((t) => t.active).length,
            upcomingHolidays: upcoming.length,
        };

        const statusDonut = [
            { name: "Pending", value: pending.length, color: STATUS_COLORS.Pending },
            { name: "Approved", value: approved.length, color: STATUS_COLORS.Approved },
            { name: "Rejected", value: rejected.length, color: STATUS_COLORS.Rejected },
        ].filter((d) => d.value > 0);

        let paidDays = 0;
        let noPayDays = 0;
        live.forEach((x) => {
            const t = typeMap[x.leaveTypeId];
            if (t && !t.paid) noPayDays += x.days;
            else paidDays += x.days;
        });
        const paidSplit = [
            { name: "Paid", value: r2(paidDays), color: PAID_COLORS.Paid },
            { name: "No Pay", value: r2(noPayDays), color: PAID_COLORS["No Pay"] },
        ].filter((d) => d.value > 0);

        // requests per month, by the month the leave starts
        const monthMap = {};
        requests.forEach((x) => {
            if (!x.from) return;
            const d = toDate(x.from);
            if (isNaN(d.getTime())) return;
            const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
            if (!monthMap[key]) {
                monthMap[key] = {
                    key,
                    label: d.toLocaleDateString("en-GB", { month: "short", year: "numeric" }),
                    requests: 0,
                    days: 0,
                };
            }
            monthMap[key].requests += 1;
            monthMap[key].days += x.days;
        });
        const monthlyTrend = Object.values(monthMap)
            .sort((a, b) => a.key.localeCompare(b.key))
            .map((m) => ({ ...m, days: r2(m.days) }));

        const typeDays = {};
        live.forEach((x) => {
            const name = typeMap[x.leaveTypeId]?.name || `Type ${x.leaveTypeId || "?"}`;
            typeDays[name] = (typeDays[name] || 0) + x.days;
        });
        const daysByType = Object.entries(typeDays)
            .map(([name, value]) => ({ name, value: r2(value) }))
            .sort((a, b) => b.value - a.value);

        const empDays = {};
        live.forEach((x) => {
            const key = x.serviceNo || "Unknown";
            empDays[key] = (empDays[key] || 0) + x.days;
        });
        const topEmployees = Object.entries(empDays)
            .map(([serviceNo, days]) => ({ serviceNo, days: r2(days) }))
            .sort((a, b) => b.days - a.days)
            .slice(0, 6);

        const holMap = {};
        holidays.forEach((h) => {
            const key = h.type || "Other";
            holMap[key] = (holMap[key] || 0) + 1;
        });
        const holidayTypes = Object.entries(holMap).map(([name, value]) => ({ name, value }));

        // soonest leave first, so the next one to deal with is at the top
        const pendingQueue = [...pending]
            .sort((a, b) => (a.from || "9999").localeCompare(b.from || "9999"))
            .slice(0, 6)
            .map((x) => ({ ...x, typeName: typeMap[x.leaveTypeId]?.name || "—" }));

        return {
            kpis, statusDonut, paidSplit, monthlyTrend, daysByType,
            topEmployees, holidayTypes, pendingQueue, nextHolidays: upcoming.slice(0, 6),
        };
    }, [requests, types, holidays, typeMap]);

    const { kpis } = analytics;

const stats = [
    { label: "Pending Requests", value: kpis.pending, sub: `${fmtDays(kpis.pendingDays)} awaiting`, icon: PendingIcon, color: "#f59e0b" },
    { label: "Approved", value: kpis.approved, sub: `${fmtDays(kpis.approvedDays)} granted`, icon: ApprovedIcon, color: "#059669" },
    { label: "Rejected", value: kpis.rejected, sub: `${kpis.approvalRate.toFixed(0)}% approval rate`, icon: RejectedIcon, color: "#dc2626" },
    { label: "Total Days", value: kpis.totalDays, sub: "Across all requests", icon: DaysIcon, color: "#2563eb" },
    { label: "Employees", value: kpis.employees, sub: "Have applied for leave", icon: EmployeesIcon, color: "#7c3aed" },
    { label: "On Leave Today", value: kpis.onLeaveToday, sub: "Approved & in range", icon: OnLeaveIcon, color: "#ea580c" },
    { label: "Leave Types", value: kpis.activeTypes, sub: `${types.length} configured`, icon: TypesIcon, color: "#4f46e5" },
    { label: "Upcoming Holidays", value: kpis.upcomingHolidays, sub: "From today onward", icon: HolidayIcon, color: "#0284c7" },
];

return (
    <Box sx={{ width: "100%", minHeight: "100vh" }}>
        <HeaderBannerPage />
        <Box sx={{ maxWidth: 1400, mx: "auto", mt: 4, px: 2, pb: 4 }}>
        {/* Banner */}
        <Paper
            elevation={0}
            sx={{
            p: 3, mb: 3, borderRadius: "12px",
            background: "linear-gradient(135deg, #1e0153 0%, #154c79 100%)",
            color: "#ffffff", display: "flex", justifyContent: "space-between",
            alignItems: "center", flexWrap: "wrap", gap: 2,
            boxShadow: "0px 4px 12px rgba(30, 1, 83, 0.15)",
        }}
        >
        <Box>
            <Chip
                label="LEAVE MANAGEMENT"
                size="small"
                sx={{ fontWeight: "bold", mb: 1, fontSize: "11px", letterSpacing: "0.5px", backgroundColor: "rgba(255,255,255,0.18)", color: "#fff" }}
            />
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5, fontFamily: "'Poppins', sans-serif" }}>
                Leave Overview
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.85, fontSize: "13px" }}>
                {requests.length} requests · {kpis.activeTypes} active leave types
            </Typography>
        </Box>
        <Button
            onClick={handleRefresh}
            disabled={refreshing}
            startIcon={<RefreshIcon sx={refreshing ? { animation: "spin 1s linear infinite", "@keyframes spin": { to: { transform: "rotate(360deg)" } } } : {}} />}
            sx={{
                height: 38, px: 2.5, borderRadius: "8px", fontWeight: "bold", textTransform: "none",
                backgroundColor: "#fff", color: NAVY,
                "&:hover": { backgroundColor: "#e8eefc" },
                "&.Mui-disabled": { backgroundColor: "rgba(255,255,255,0.6)", color: NAVY },
            }}
        >
        Refresh
        </Button>
        </Paper>

        {/* KPI row */}
        <Box
            sx={{
            display: "grid", gap: 2, mb: 3,
            gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(4, 1fr)", xl: "repeat(8, 1fr)" },
        }}
        >
        {stats.map((s) => <StatCard key={s.label} {...s} />)}
        </Box>

        {/* Row 1 */}
        <Box
            sx={{
            display: "grid", gap: 2, mb: 3,
            gridTemplateColumns: { xs: "minmax(0, 1fr)", lg: "minmax(0, 2fr) minmax(0, 1fr) minmax(0, 1fr)" },
        }}
        >
        <ChartCard title="Leave Requests by Month" subtitle="Applications and days requested" empty={!analytics.monthlyTrend.length}>
            <ResponsiveContainer width="100%" height={240}>
                <BarChart data={analytics.monthlyTrend} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#94a3b8" }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#94a3b8" }} />
                <Tooltip contentStyle={tooltipStyle} />
                <Bar dataKey="requests" name="Requests" fill="#2563eb" radius={[4, 4, 0, 0]} maxBarSize={34} />
                <Bar dataKey="days" name="Days" fill="#10b981" radius={[4, 4, 0, 0]} maxBarSize={34} />
                </BarChart>
            </ResponsiveContainer>
        </ChartCard>

        <ChartCard
            title="Requests by Status"
            subtitle={`${kpis.pending + kpis.approved + kpis.rejected} in total`}
            empty={!analytics.statusDonut.length}
            footer={<Legend items={analytics.statusDonut} />}
        >
            <DonutChart data={analytics.statusDonut} />
        </ChartCard>

        <ChartCard
            title="Paid vs No Pay"
            subtitle="Days, by leave type"
            empty={!analytics.paidSplit.length}
            footer={<Legend items={analytics.paidSplit} />}
        >
            <DonutChart data={analytics.paidSplit} />
        </ChartCard>
        </Box>

        {/* Row 2 */}
        <Box
            sx={{
            display: "grid", gap: 2, mb: 3,
            gridTemplateColumns: { xs: "minmax(0, 1fr)", lg: "repeat(3, minmax(0, 1fr))" },
        }}
        >
        <ChartCard title="Days by Leave Type" subtitle="Approved and pending" empty={!analytics.daysByType.length}>
            <ResponsiveContainer width="100%" height={230}>
                <BarChart data={analytics.daysByType} layout="vertical" margin={{ top: 4, right: 16, left: 8, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f1f5f9" />
                <XAxis type="number" tick={{ fontSize: 11, fill: "#94a3b8" }} />
                <YAxis type="category" dataKey="name" width={100} tick={{ fontSize: 10, fill: "#94a3b8" }} />
                <Tooltip contentStyle={tooltipStyle} formatter={(v) => [`${v} days`, "Days"]} />
                <Bar dataKey="value" fill="#7c3aed" radius={[0, 4, 4, 0]} maxBarSize={18} />
                </BarChart>
            </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Top Employees by Leave" subtitle="Most days requested" empty={!analytics.topEmployees.length}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {analytics.topEmployees.map((e, i) => {
                const max = Math.max(1, ...analytics.topEmployees.map((x) => x.days));
                const top = i < 3;
                return (
                <Box key={e.serviceNo} sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Box
                        sx={{
                        width: 24, height: 24, flexShrink: 0, borderRadius: "50%", color: "#fff",
                        fontSize: 11, fontWeight: 700, display: "flex", alignItems: "center",
                        justifyContent: "center", backgroundColor: top ? "#154c79" : "#cbd5e1",
                    }}
                    >
                    {i + 1}
                    </Box>
                    <Typography sx={{ width: 80, flexShrink: 0, fontSize: 12.5, fontWeight: top ? 600 : 400, color: top ? "#154c79" : "#475569" }} noWrap>
                        {e.serviceNo}
                    </Typography>
                    <Box sx={{ flex: 1, height: 6, backgroundColor: "#f1f5f9", borderRadius: 3, position: "relative" }}>
                      <Box sx={{ position: "absolute", inset: "0 auto 0 0", width: `${(e.days / max) * 100}%`, backgroundColor: "#154c79", borderRadius: 3 }} />
                    </Box>
                    <Typography sx={{ width: 44, textAlign: "right", fontSize: 12.5, fontWeight: 700, color: "#334155" }}>
                        {e.days}d
                    </Typography>
                </Box>
                );
            })}
            </Box>
        </ChartCard>

        <ChartCard title="Holidays by Type" subtitle={`${holidays.length} in the calendar`} empty={!analytics.holidayTypes.length}>
            <ResponsiveContainer width="100%" height={230}>
                <BarChart data={analytics.holidayTypes} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#94a3b8" }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#94a3b8" }} />
                <Tooltip contentStyle={tooltipStyle} formatter={(v) => [v, "Holidays"]} />
                <Bar dataKey="value" fill="#0ea5e9" radius={[4, 4, 0, 0]} maxBarSize={40} />
                </BarChart>
            </ResponsiveContainer>
        </ChartCard>
        </Box>

        {/* Row 3 */}
        <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { xs: "minmax(0, 1fr)", lg: "repeat(2, minmax(0, 1fr))" } }}>
            {/* <Paper elevation={0} sx={cardSx}> */}
            <Paper elevation={0} sx={{ ...cardSx, minWidth: 0, overflow: "hidden", boxSizing: "border-box" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
            <QueueIcon sx={{ fontSize: 20, color: "#d97706" }} />
            <Typography sx={{ fontSize: 15, fontWeight: 700, color: NAVY }}>Pending Approvals</Typography>
            {kpis.pending > 0 && (
                <Chip label={kpis.pending} size="small" sx={{ ml: "auto", fontWeight: 700, backgroundColor: "#fef3c7", color: "#b45309" }} />
            )}
            </Box>
            {!analytics.pendingQueue.length ? (
            <Box sx={{ height: 160, display: "flex", alignItems: "center", justifyContent: "center", color: "#94a3b8", fontSize: 13 }}>
                Nothing awaiting approval
            </Box>
            ) : (
                analytics.pendingQueue.map((x, i) => (
                <React.Fragment key={x.formNo}>
                    {i > 0 && <Divider />}
                    <ListRow
                    title={`${x.serviceNo} · ${x.typeName}`}
                    subtitle={`${fmtDate(x.from)} – ${fmtDate(x.to)}${x.reason ? ` · ${x.reason}` : ""}`}
                    right={x.halfDay ? "Half day" : `${x.days}d`}
                    rightSx={{ fontWeight: 700, color: "#b45309", backgroundColor: "#fffbeb", border: "1px solid #fde68a", px: 1, py: 0.25, borderRadius: "6px" }}
                />
                </React.Fragment>
            ))
            )}
        </Paper>

        <Paper elevation={0} sx={{ ...cardSx, minWidth: 0, overflow: "hidden", boxSizing: "border-box" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                <HolidayIcon sx={{ fontSize: 20, color: "#0284c7" }} />
                <Typography sx={{ fontSize: 15, fontWeight: 700, color: NAVY }}>Upcoming Holidays</Typography>
            </Box>
            {!analytics.nextHolidays.length ? (
            <Box sx={{ height: 160, display: "flex", alignItems: "center", justifyContent: "center", color: "#94a3b8", fontSize: 13 }}>
                No upcoming holidays
            </Box>
            ) : (
                analytics.nextHolidays.map((h, i) => (
                <React.Fragment key={h.id}>
                    {i > 0 && <Divider />}
                    <ListRow
                    title={h.name}
                    subtitle={`${h.type}${h.remarks ? ` · ${h.remarks}` : ""}`}
                    right={fmtDate(h.date)}
                    rightSx={{ color: "#64748b" }}
                />
                </React.Fragment>
            ))
            )}
        </Paper>
        </Box>
    </Box>
    </Box>
    );
};
export default LeaveDashboard;
